export default {
  // AI Recognition
  aiGlobalRecognition: {
    buttonText: 'AI Recognition',
    loadingText: 'Recognizing...',
    successTemplate: 'Recognition complete, {filled} fields filled',
    softFailure: 'Filling failed, please check input text',
    requestError: 'Request failed: {status} {error}',
    fieldRecognized: 'Recognized',
    modal: {
      title: 'AI Recognition',
      width: '600px',
      maskClosable: false
    },
    content: {
      description: 'Paste text content for AI to automatically extract logistics information and fill fields.',
      supportedItems: 'Supported: Port info, carrier, container type/count, weight, transit, route, cargo type, service terms, etc.',
      processing: 'AI is recognizing, please wait...'
    },
    placeholder: 'Paste text content, e.g.:\nFrom CNSHA/Shanghai to USLAX/Los Angeles\nCarrier: Maersk\nContainer: 2x20GP + 1x40HC\nWeight: 1200 KGS\nDirect, Service Terms: DDP\nCargo Type: General',
    buttons: {
      cancel: 'Cancel',
      confirm: 'Confirm'
    },
    messages: {
      success: 'AI Recognition complete, fields filled automatically',
      error: 'AI Recognition failed, please try again',
      emptyInput: 'Please enter text to recognize'
    }
  },

  // Reset Functionality
  reset: {
    buttonText: 'Reset',
    confirmTitle: 'Confirm Reset',
    confirmMessage: 'Are you sure you want to reset and clear all fields?',
    confirmButton: 'Confirm',
    cancelButton: 'Cancel',
    successMessage: 'All fields have been reset'
  },

  // Enums
  enums: {
    // Pricing Nature
    pricingNature: {
      selfPricing: 'FAK',
      customerPricing: 'Customer Pricing',
      overseasAgentPricing: 'Overseas Agent Pricing',
      noPricing: 'No Pricing',
      peerPricing: 'Peer Pricing',
      bcoContract: 'NAC/FIX',
      afcPricing: 'AFC Pricing',
      mixedPricing: 'Blended Pricing'
    },

    // Commodity
    commodity: {
      fak: 'FAK',
      dangerousGoods: 'Dangerous Goods',
      specialContainer: 'Special Container',
      frozenGoods: 'Frozen Goods',
      chemicalGoods: 'Chemical Goods',
      textileGoods: 'Textile Goods',
      other: 'Other'
    },

    // Space Guarantee
    spaceGuarantee: {
      guarantee20: 'Yes (20 TEU/Month)',
      guarantee50: 'Yes (50 TEU/Month)',
      guarantee100: 'Yes (100 TEU/Month)',
      guarantee300: 'Yes (300 TEU/Month)',
      noGuarantee: 'No'
    },

    // Contract Status
    contractStatus: {
      enabled: 'Enabled',
      disabled: 'Disabled'
    }
  },

  // Last Mile Detail Page
  lastmileDetail: {
    pageTitle: {
      create: 'Add Last Mile Rate',
      copy: 'Copy Last Mile Rate',
      edit: 'Edit Last Mile Rate',
      view: 'Last Mile Rate Details'
    },
    buttons: {
      back: 'Back',
      addAddress: 'Add Address',
      removeAddress: 'Remove Address',
      expandAddress: 'Expand',
      collapseAddress: 'Collapse'
    },
    basicInfo: {
      title: 'Basic Information',
      priceNo: 'Last Mile Rate No.',
      priceNoPlaceholder: {
        copy: 'Regenerated after copy, e.g., LMR2506250001',
        create: 'Generated by system, e.g., LMR2506250001'
      }
    },
    address: {
      title: 'Delivery Address',
      addressItem: 'Delivery Address {index}',
      addressType: 'Address Type',
      addressTypeOptions: {
        thirdParty: 'Third Party Address',
        amazonWarehouse: 'Amazon Warehouse',
        easyWarehouse: 'ECang',
        notSelected: 'Not Selected'
      },
      warehouseCode: 'Warehouse Code',
      warehouseCodePlaceholder: {
        amazon: 'Select Amazon Warehouse',
        easy: 'Select ECang'
      },
      postalCode: 'Postal Code',
      detailAddress: 'Detail Address',
      country: 'Country',
      state: 'State',
      city: 'City',
      district: 'District',
      preview: {
        addressType: 'Address Type: ',
        warehouseCode: 'Warehouse Code: ',
        postalCode: 'Postal Code: ',
        detailAddress: 'Detail Address: '
      }
    },
    destinationInfo: {
      title: 'Destination Info',
      pod: 'Destination Port (POD)',
      podPlaceholder: 'Select destination port',
      podRequired: 'Destination port is required',
      agent: 'Agent Name',
      agentPlaceholder: 'Enter agent name',
      agentRequired: 'Agent name is required',
      etd: 'ETD',
      etdPlaceholder: 'Select estimated time of departure',
      eta: 'ETA',
      etaPlaceholder: 'Select estimated time of arrival',
      validPeriod: 'Validity Period',
      validPeriodPlaceholder: ['Start Date', 'End Date'],
      validPeriodRequired: 'Validity period is required',
      remarks: 'Remarks',
      remarksPlaceholder: 'Enter remarks'
    },
    rateInfo: {
      title: 'Rate Information',
      containerBilling: {
        title: 'Container-based Billing Items',
        addItem: 'Add Item',
        removeItem: 'Remove Item',
        minItemsWarning: 'At least one rate item is required'
      },
      nonContainerBilling: {
        title: 'Non-container Billing Items',
        addItem: 'Add Item',
        removeItem: 'Remove Item',
        minItemsWarning: 'At least one billing item is required'
      },
      columns: {
        feeName: 'Fee Name',
        feeNamePlaceholder: 'Select fee name',
        currency: 'Currency',
        currencyPlaceholder: 'Auto-filled',
        unit: 'Unit',
        unitPlaceholder: 'Select unit',
        price: 'Unit Price',
        pricePlaceholder: 'Enter unit price',
        specialNote: 'Special Note',
        specialNotePlaceholder: 'Special notes'
      },
      containerTypes: {
        '20gp': '20GP',
        '40gp': '40GP',
        '40hc': '40HC',
        '45hc': '45HC',
        '40nor': '40NOR',
        '20nor': '20NOR',
        '20hc': '20HC',
        '20tk': '20TK',
        '40tk': '40TK',
        '20ot': '20OT',
        '40ot': '40OT',
        '20fr': '20FR',
        '40fr': '40FR'
      }
    },
    feeNames: {
      lastMileFee: 'Last Mile Fee',
      deliveryFee: 'Delivery Fee',
      storageFee: 'Storage Fee',
      handlingFee: 'Handling Fee',
      fuelFee: 'Fuel Fee',
      documentFee: 'Document Fee',
      customsFee: 'Customs Fee',
      inspectionFee: 'Inspection Fee',
      operationFee: 'Operation Fee',
      insuranceFee: 'Insurance Fee'
    },
    messages: {
      missingParams: 'Missing required parameters',
      dataLoadFailed: 'Failed to load data',
      copyDataLoadFailed: 'Failed to load data for copy',
      atLeastOneAddress: 'At least one address is required',
      priceFormat: 'LMR2506250001'
    }
  },

  // Root-level common fields
  freeContainerDays: 'Free Container Days',
  demurrageFreeDays: 'Free Demurrage Days',

  // Delivery Address Type
  deliveryAddressType: {
    thirdParty: 'Third Party Address',
    amazon: 'Amazon Warehouse',
    yicang: 'Ecang'
  },

  // Cost Trial
  costTrial: {
    title: 'Cost Trial',
    boxTypeConfig: 'Container Type Config',
    feeDetails: 'Fee Details',
    feeSummary: 'Fee Summary',
    totalFee: 'Total Fee',
    toCny: 'Equivalent CNY',
    exchangeRate: 'Exchange Rate',
    loadRateFailed: 'Failed to load exchange rate, please try again',
    exchangeRateValidPeriod: 'Exchange Rate Validity',
    rateValidPeriod: 'Rate Validity',
    contactAdmin: 'If you have questions, please contact admin',
    noSuitableExchangeRate: 'No suitable exchange rate to convert to CNY',
    exchangeRateNotExist: 'Exchange rate does not exist',
    messages: {
      noFeeData: 'No fee data available for cost trial'
    },
    placeholders: {
      selectBoxType: 'Select container type'
    },
    columns: {
      feeCategory: 'Fee Category',
      feeName: 'Fee Name',
      currency: 'Currency',
      bill: 'Bill',
      box: 'Box'
    },
    subtotal: 'Subtotal',
    categoryMap: {
      trunkLine: 'Trunk Line',
      prePort: 'Pre-carriage',
      lastMile: 'Last Mile'
    }
  },

  // Supplier Selection
  supplierSelection: {
    title: 'Select Quote Target',
    tabs: {
      business: 'Business',
      supplier: 'Supplier'
    },
    searchPlaceholder: 'Search name, contact keywords',
    managerSearchPlaceholder: 'Search name / phone / email',
    noManagers: 'No matching managers found',
    filter: 'Filter',
    more: 'More',
    selectAll: 'Select All',
    preferred: 'Preferred Suppliers',
    region: 'Region',
    contactInfo: 'Contact Info',
    contact: 'Contact',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    establishedDate: 'Established',
    registeredCapital: 'Registered Capital',
    evaluation: 'Supplier Evaluation',
    reviews: '{count} reviews',
    productQuality: 'Product Quality',
    priceCompetitiveness: 'Price Competitiveness',
    deliveryTimeliness: 'Delivery Timeliness',
    serviceAttitude: 'Service Attitude',
    noData: 'No Data',
    selectSupplier: 'Select a supplier to view details',
    selectedCount: '{count} suppliers selected',
    cancel: 'Cancel',
    confirm: 'Confirm Selection',
    confirmOnly: 'Confirm',
    notSet: 'Not Set',
    noContacts: 'No contacts available',
    selectAtLeastOneContact: 'Please select at least one contact',
    loadFailed: 'Failed to load supplier list',
    selectManagerFirst: 'Please select a business manager',
    loadManagersFailed: 'Failed to load business managers',
    messages: {
      missingInquiryId: 'Missing inquiry ID',
      selectSupplierContactFirst: 'Please select supplier contacts first',
      sendEmailSuccess: 'Inquiry emails sent to {count} contacts',
      sendEmailFailed: 'Failed to send inquiry emails, please try again'
    }
  }
}
